import { useEffect } from "react";
import { useRouter } from "next/compat/router";

import { useModalContext } from "@fwa/src/contexts/ModalContext";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import {
  redirectStateFromUrl,
  urlParamsObjectClearFromParam,
} from "@fwa/src/utils/urlUtils";
import { isBrowser } from "@fwa/src/utils/browserUtils";
import { useTracking } from "@fwa/src/hooks/useTracking";

import { ModalNotLoggedIn } from "@fwa/src/components/ModalNotLoggedIn";

import type { FundraisingPageType } from "@fwa/src/types";

export const EmailJourneyModalManager = ({
  page,
}: {
  page: FundraisingPageType;
}) => {
  const [modalState, setModalState] = useModalContext();
  const [fundraiserState] = useFundraiserContext();
  const { loggedInStatus } = fundraiserState;
  const { trackError } = useTracking();

  const router = useRouter();

  // If we are logged in lets clean up the querystring to remove the email journey params.
  // This is useful if the logged in user copies the url in the browser window and shares it.
  // We don't want the email journey modals to be triggered for anyone else that visits that url. We also want to remove the email journey params from the url as soon as possible to avoid any flickering of the modals for the user that is logged in.
  useEffect(() => {
    if (!router || !isBrowser || !page || loggedInStatus !== "loggedIn") {
      return;
    }
    const { isFromEmail } = redirectStateFromUrl(window.location.href);

    if (!isFromEmail) {
      return;
    }

    router
      ?.push(
        {
          query: urlParamsObjectClearFromParam(router.query),
        },
        undefined,
        {
          shallow: true,
        },
      )
      .catch((error) => {
        trackError(error, {
          component: "EmailJourneyModalForPage",
          function: "clearEmailJourneyParams",
        });
      });
  }, [router, loggedInStatus, page, trackError]);

  // if you are logged out and from email show the modal to prompt log in
  useEffect(() => {
    if (
      !router ||
      !isBrowser ||
      !page ||
      loggedInStatus !== "loggedOut" ||
      modalState
    ) {
      return;
    }

    const { isEmJourney, isAmJourney, isFromEmail } = redirectStateFromUrl(
      window.location.href,
    );

    // we want the EM and AM modals to take precedence over the email journey modal
    if (isEmJourney || isAmJourney || !isFromEmail) {
      return;
    }

    const isNewUser = !page.fundraiser.isActivated || false; // if the fundraiser isn't activated they must be a new user

    const dismissModal = () => {
      router
        ?.push(
          {
            // clear the 'from' email journey param from the query string when we dismiss the modal
            query: urlParamsObjectClearFromParam(router.query),
          },
          undefined,
          {
            shallow: true,
          },
        )
        .then(() => {
          setModalState(null);
          return;
        })
        .catch((error) => {
          trackError(error, {
            component: "EmailJourneyModalForPage",
            function: "dismissModal",
          });
        });
    };

    setModalState(
      <ModalNotLoggedIn
        closeFunction={dismissModal}
        isNewUser={isNewUser}
        isFromEmail={true}
        modalName="From email modal not logged in"
        componentName="email-journey-modal-for-page"
      />,
    );
  }, [router, loggedInStatus, setModalState, page, trackError, modalState]);

  return null;
};
